/**
 * IT Admin → Ticket search (21 Oct): find any incident or service request in
 * ITSM by any field, open or closed.
 *
 * Asked for by Helle Froulund Hansen (Systems Support): "search for tickets:
 * by the subject, by keywords, by build, entity, system, all the fields".
 * Every filter is optional and they combine; pick lists offer Ivanti's own
 * values (`/it-admin/ticket-search/options`). The search itself runs on the
 * server against Ivanti, read only, at most 500 rows (see
 * `api/services/ticket_search.py`).
 *
 * The last search and up to 12 named searches are kept in this browser, per
 * signed-in person (`criteria.ts`); nothing is stored on the server.
 */

import { Bookmark, Icon, RotateCcw, Search } from "@norican/icons";
import {
  Button,
  Checkbox,
  Chip,
  DatePicker,
  Dialog,
  Field,
  FilterSelect,
  Input,
  parseDate,
  Toggle,
  ToggleGroup,
  Typography,
} from "@norican/react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";

import { ErrorState, LoadingState, SectionCard } from "@/components/common";
import { EmployeePicker } from "@/features/tickets";
import { useT } from "@/i18n";
import { type TicketSearchOptions, useMe } from "@/lib/api";

import { useTicketSearch, useTicketSearchOptions } from "../api";
import {
  activeFilterCount,
  type Criteria,
  type DateRange,
  emptyCriteria,
  type ListFilter,
  numberIsValid,
  parseCriteria,
  parseSaved,
  removeSaved,
  type SavedSearch,
  type SearchKind,
  type StatusGroup,
  storageKeys,
  toQuery,
  upsertSaved,
} from "./criteria";
import { SearchResults } from "./search-results";

/** Which pick list feeds each list filter. */
const LIST_SOURCE: Record<ListFilter, (o: TicketSearchOptions) => string[]> = {
  statuses: (o) => [
    ...new Set([...o.incident_statuses, ...o.request_statuses].map((s) => s.value)),
  ],
  teams: (o) => o.teams,
  priorities: (o) => o.priorities,
  categories: (o) => o.categories,
  services: (o) => o.services,
  offerings: (o) => o.offerings,
  sources: (o) => o.sources,
  builds: (o) => o.builds,
  entities: (o) => o.entities,
  systems: (o) => o.systems,
  projects: (o) => o.projects,
};

/** The pick lists shown first; the rest sit behind "More filters". */
const MAIN_LISTS: ListFilter[] = ["teams", "systems", "entities", "builds", "projects"];
const MORE_LISTS: ListFilter[] = [
  "statuses",
  "priorities",
  "categories",
  "services",
  "offerings",
  "sources",
];

/** "Team", "Team: CRM Team" or "Team (3)". */
function listSummary(label: string, selected: string[]): string {
  if (selected.length === 0) {
    return label;
  }
  return selected.length === 1 ? `${label}: ${selected[0]}` : `${label} (${selected.length})`;
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage full or blocked: the search still works, it just is not remembered.
  }
}

export function TicketSearchPage() {
  const { t } = useT("tickets");
  const { t: tc } = useT();
  const { data: me } = useMe();
  const keys = useMemo(() => storageKeys(me?.email ?? ""), [me?.email]);
  const options = useTicketSearchOptions();

  const [criteria, setCriteria] = useState<Criteria>(() => emptyCriteria());
  const [submitted, setSubmitted] = useState<Record<string, unknown> | null>(null);
  const [saved, setSaved] = useState<SavedSearch[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  // The last search and the saved ones come back when the page opens.
  useEffect(() => {
    if (!me?.email) {
      return;
    }
    const last = readStorage(keys.last);
    if (last) {
      try {
        const restored = parseCriteria(JSON.parse(last));
        setCriteria(restored);
        setMoreOpen(MORE_LISTS.some((name) => restored.lists[name].length > 0));
      } catch {
        // A damaged entry is ignored: an empty form is the honest default.
      }
    }
    setSaved(parseSaved(readStorage(keys.saved)));
  }, [keys, me?.email]);

  const search = useTicketSearch(submitted);
  const numberOk = numberIsValid(criteria.number);
  const active = activeFilterCount(criteria);

  function update(patch: Partial<Criteria>) {
    setCriteria((current) => ({ ...current, ...patch }));
  }
  function setList(name: ListFilter, values: string[]) {
    setCriteria((current) => ({ ...current, lists: { ...current.lists, [name]: values } }));
  }
  function setDate(name: DateRange, end: "from" | "to", value: string | null) {
    setCriteria((current) => ({
      ...current,
      dates: { ...current.dates, [name]: { ...current.dates[name], [end]: value } },
    }));
  }
  function run(next: Criteria) {
    if (!numberIsValid(next.number)) {
      return;
    }
    writeStorage(keys.last, JSON.stringify(next));
    setSubmitted(toQuery(next));
  }
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    run(criteria);
  }
  function apply(item: SavedSearch) {
    setCriteria(item.criteria);
    setMoreOpen(MORE_LISTS.some((name) => item.criteria.lists[name].length > 0));
    run(item.criteria);
  }
  function storeSaved(next: SavedSearch[]) {
    setSaved(next);
    writeStorage(keys.saved, JSON.stringify(next));
  }

  const listOptions = (name: ListFilter) =>
    (options.data ? LIST_SOURCE[name](options.data) : []).map((value) => ({
      value,
      label: value,
    }));
  const listSelect = (name: ListFilter) => (
    <FilterSelect
      key={name}
      size="sm"
      options={listOptions(name)}
      value={criteria.lists[name]}
      onValueChange={(values) => setList(name, values)}
      placeholder={t(`itAdmin.ticketSearch.filter.${name}`)}
      summary={(selected) => listSummary(t(`itAdmin.ticketSearch.filter.${name}`), selected)}
      showCount={false}
      searchPlaceholder={t("itAdmin.ticketSearch.filter.searchList")}
      aria-label={t(`itAdmin.ticketSearch.filter.${name}`)}
      disabled={options.isPending}
    />
  );
  const dateRange = (name: DateRange): ReactNode => {
    const { from, to } = criteria.dates[name];
    return (
      <div className="flex flex-col gap-1" key={name}>
        <Typography variant="label">{t(`itAdmin.ticketSearch.date.${name}`)}</Typography>
        <div className="flex flex-wrap items-center gap-2">
          <DatePicker
            size="sm"
            className="w-40"
            aria-label={`${t(`itAdmin.ticketSearch.date.${name}`)}: ${t("itAdmin.ticketSearch.date.from")}`}
            value={from ? parseDate(from) : null}
            maxValue={to ? parseDate(to) : undefined}
            onValueChange={(value) => setDate(name, "from", value ? value.toString() : null)}
          />
          <Typography variant="body-sm" tone="muted">
            –
          </Typography>
          <DatePicker
            size="sm"
            className="w-40"
            aria-label={`${t(`itAdmin.ticketSearch.date.${name}`)}: ${t("itAdmin.ticketSearch.date.to")}`}
            value={to ? parseDate(to) : null}
            minValue={from ? parseDate(from) : undefined}
            onValueChange={(value) => setDate(name, "to", value ? value.toString() : null)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionCard
        title={t("itAdmin.ticketSearch.title")}
        description={t("itAdmin.ticketSearch.intro")}
      >
        <form className="flex flex-col gap-4" onSubmit={onSubmit} role="search">
          <div className="flex flex-wrap items-end gap-3">
            <Field
              name="ticket-search-keywords"
              label={t("itAdmin.ticketSearch.keywords")}
              className="min-w-64 flex-1"
            >
              <Input
                value={criteria.keywords}
                maxLength={200}
                placeholder={t("itAdmin.ticketSearch.keywordsPlaceholder")}
                onChange={(event) => update({ keywords: event.target.value })}
              />
            </Field>
            <Field
              name="ticket-search-number"
              label={t("itAdmin.ticketSearch.number")}
              error={numberOk ? undefined : t("itAdmin.ticketSearch.numberInvalid")}
              className="w-44"
            >
              <Input
                value={criteria.number}
                maxLength={20}
                inputMode="numeric"
                placeholder={t("itAdmin.ticketSearch.numberPlaceholder")}
                onChange={(event) => update({ number: event.target.value })}
              />
            </Field>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                startIcon={<Icon icon={Search} size="sm" />}
                loading={search.isFetching}
                disabled={!numberOk}
              >
                {t("itAdmin.ticketSearch.search")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                startIcon={<Icon icon={RotateCcw} size="sm" />}
                disabled={active === 0}
                onClick={() => setCriteria(emptyCriteria())}
              >
                {t("itAdmin.ticketSearch.clear")}
              </Button>
            </div>
          </div>

          <Typography variant="caption" tone="muted" className="-mt-2">
            {t("itAdmin.ticketSearch.keywordsHint")}
          </Typography>

          <Checkbox.Root
            checked={criteria.searchNotes}
            onCheckedChange={(next) => update({ searchNotes: next === true })}
            label={t("itAdmin.ticketSearch.searchNotes")}
          />

          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup.Root
              appearance="segmented"
              size="sm"
              value={[criteria.kind]}
              onValueChange={(next) => {
                const [value] = next;
                if (value) {
                  update({ kind: value as SearchKind });
                }
              }}
              aria-label={t("itAdmin.ticketSearch.kind.label")}
            >
              {(["all", "incident", "request"] as const).map((kind) => (
                <Toggle key={kind} value={kind}>
                  {t(`itAdmin.ticketSearch.kind.${kind}`)}
                </Toggle>
              ))}
            </ToggleGroup.Root>
            <ToggleGroup.Root
              appearance="segmented"
              size="sm"
              value={[criteria.statusGroup]}
              onValueChange={(next) => {
                const [value] = next;
                if (value) {
                  update({ statusGroup: value as StatusGroup });
                }
              }}
              aria-label={t("itAdmin.ticketSearch.statusGroup.label")}
            >
              {(["any", "open", "closed"] as const).map((group) => (
                <Toggle key={group} value={group}>
                  {t(`itAdmin.ticketSearch.statusGroup.${group}`)}
                </Toggle>
              ))}
            </ToggleGroup.Root>
            {MAIN_LISTS.map(listSelect)}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setMoreOpen((open) => !open)}
              aria-expanded={moreOpen}
            >
              {moreOpen
                ? t("itAdmin.ticketSearch.fewerFilters")
                : t("itAdmin.ticketSearch.moreFilters")}
            </Button>
          </div>

          {moreOpen ? (
            <div className="border-border flex flex-col gap-4 rounded-md border p-4">
              <div className="flex flex-wrap items-center gap-3">{MORE_LISTS.map(listSelect)}</div>
              <Typography variant="caption" tone="muted">
                {t("itAdmin.ticketSearch.kindOnlyNote")}
              </Typography>
              <div className="grid gap-4 md:grid-cols-2">
                <Field name="ticket-search-owner" label={t("itAdmin.ticketSearch.owner")}>
                  <EmployeePicker
                    name="ticket-search-owner"
                    value={criteria.owner?.id ?? null}
                    defaultDisplayValue={criteria.owner?.name ?? null}
                    onChange={(entry) =>
                      update({ owner: entry ? { id: entry.id, name: entry.display_name } : null })
                    }
                    placeholder={t("itAdmin.ticketSearch.personPlaceholder")}
                    aria-label={t("itAdmin.ticketSearch.owner")}
                  />
                </Field>
                <Field name="ticket-search-customer" label={t("itAdmin.ticketSearch.customer")}>
                  <EmployeePicker
                    name="ticket-search-customer"
                    value={criteria.customer?.id ?? null}
                    defaultDisplayValue={criteria.customer?.name ?? null}
                    onChange={(entry) =>
                      update({
                        customer: entry ? { id: entry.id, name: entry.display_name } : null,
                      })
                    }
                    placeholder={t("itAdmin.ticketSearch.personPlaceholder")}
                    aria-label={t("itAdmin.ticketSearch.customer")}
                  />
                </Field>
              </div>
              <div className="flex flex-wrap gap-6">
                {(["created", "resolved", "next_step"] as const).map(dateRange)}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="label" className="me-1">
              {t("itAdmin.ticketSearch.saved.label")}
            </Typography>
            {saved.length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                {t("itAdmin.ticketSearch.saved.none")}
              </Typography>
            ) : (
              saved.map((item) => (
                <Chip
                  key={item.name}
                  size="sm"
                  pressed={false}
                  onPressedChange={() => apply(item)}
                  onRemove={() => storeSaved(removeSaved(saved, item.name))}
                  removeLabel={t("itAdmin.ticketSearch.saved.delete", { name: item.name })}
                >
                  {item.name}
                </Chip>
              ))
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              startIcon={<Icon icon={Bookmark} size="sm" />}
              disabled={active === 0}
              onClick={() => {
                setSaveName("");
                setSaveOpen(true);
              }}
            >
              {t("itAdmin.ticketSearch.saved.save")}
            </Button>
          </div>
        </form>
      </SectionCard>

      {options.isError ? (
        <ErrorState error={options.error} onRetry={() => void options.refetch()} />
      ) : null}

      {submitted === null ? (
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.ticketSearch.results.start")}
        </Typography>
      ) : search.isPending ? (
        <LoadingState />
      ) : search.isError ? (
        <ErrorState error={search.error} onRetry={() => void search.refetch()} />
      ) : (
        <SearchResults result={search.data} canOpenInHelpdesk={me?.role === "admin"} />
      )}

      <Dialog.Root open={saveOpen} onOpenChange={setSaveOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Popup size="sm">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (saveName.trim()) {
                  storeSaved(upsertSaved(saved, saveName, criteria));
                  setSaveOpen(false);
                }
              }}
            >
              <Dialog.Header>
                <Dialog.Title>{t("itAdmin.ticketSearch.saved.save")}</Dialog.Title>
                <Dialog.CloseButton label={tc("button.close")} />
              </Dialog.Header>
              <Dialog.Body>
                <Field name="ticket-search-save-name" label={t("itAdmin.ticketSearch.saved.name")}>
                  <Input
                    value={saveName}
                    maxLength={60}
                    autoFocus
                    placeholder={t("itAdmin.ticketSearch.saved.namePlaceholder")}
                    onChange={(event) => setSaveName(event.target.value)}
                  />
                </Field>
              </Dialog.Body>
              <Dialog.Footer>
                <Button type="button" variant="ghost" onClick={() => setSaveOpen(false)}>
                  {tc("button.cancel")}
                </Button>
                <Button type="submit" disabled={!saveName.trim()}>
                  {tc("button.save")}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
